import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';

import { useEditor } from 'components/Canvas';
import CustomScroller from 'components/CustomScroller';
import TemplateFilter from './components/TemplateFilter';
import MigrateSizeFormatModal from './components/MigrateSizeFormatModal';
import Icons from '../../Icons';
// import PanelTitle from './components/PanelTitle';
import { downloadFonts, getTemplateFonts } from 'helpers/canvasHelpers';
import { isPresignedURLExpired } from 'helpers/presetDateHelpers';

import {
  Box,
  Input,
  Text,
  InputGroup,
  InputLeftElement,
  useDisclosure,
} from '@chakra-ui/react';

import useTemplateEditorContext from 'scenes/templateEditor/hooks/useTemplateEditorContext';
import {
  selectPremadeTemplates,
  selectSizeFormats,
} from 'modules/templates/templatesSlice';

import {
  useLazyGetTemplateByIdQuery,
  useGetUserFontsQuery,
} from 'modules/templates/templatesApi';

const getThumbURL = name => {
  const thumbName = name.replace(/\s+/g, '-').toLowerCase();
  const baseURL = 'http://dzqi3b5s5jl7u.cloudfront.net/standard_templates/';
  const thumbURL = `${baseURL}${thumbName}-preview-thumb.jpg`;
  return thumbURL;
};

function Templates() {
  const editor = useEditor();

  const [payload, setPayload] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Selectors
  const sizeFormats = useSelector(selectSizeFormats);
  const premadeTemplates = useSelector(selectPremadeTemplates);

  const { setIsTemplateLoading } = useTemplateEditorContext();
  const [templates, setTemplates] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState({});

  const [getTemplateById] = useLazyGetTemplateByIdQuery();
  const { data: userFontsData } = useGetUserFontsQuery();

  useEffect(() => {
    if (premadeTemplates) {
      const sortedTemplates = [...premadeTemplates.data].sort((a, b) => {
        const keyName = 'name';
        if (a[keyName] > b[keyName]) return 1;
        if (a[keyName] < b[keyName]) return -1;
        return 0;
      });

      if (!sortedTemplates) return;
      const templates = sortedTemplates.map(sortedTemplate => {
        return {
          ...sortedTemplate,
          previewURL: `url(${getThumbURL(sortedTemplate.name)})`,
        };
      });
      setTemplates(templates);
    }
  }, [premadeTemplates]);

  const loadTemplate = async args => {
    const isTemplateEnabled = sizeFormats.find(
      sf => sf.label === args.template.size_format.label
    );
    if (isTemplateEnabled) {
      setIsTemplateLoading(true);
      const bg_img_url = args.template.bg_img?.original_url || '';
      // Handle font expiration
      if (bg_img_url && bg_img_url.match(/s3.amazonaws.com/g)) {
        const isExpired = isPresignedURLExpired(bg_img_url);
        if (isExpired) {
          try {
            const template = await getTemplateById(args.template.id).unwrap();
            args.template = cloneDeep(template);
          } catch (error) {
            console.log('Fail to load template: ', error);
          }
        }
      }

      const fontsList = getTemplateFonts(args, userFontsData);
      await downloadFonts(fontsList);
      await editor.importFromJSON(args);
      setIsTemplateLoading(false);
    } else {
      setPayload(args);
      onOpen();
    }
  };

  const isFiltered = element => {
    const ele = element.toLowerCase();
    const res = query.toLowerCase();
    return ele.includes(res) || ele === res;
  };

  return (
    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
      <MigrateSizeFormatModal
        loadTemplate={loadTemplate}
        args={payload}
        sizeFormats={sizeFormats}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
      />
      <Box sx={{ padding: '1.5rem' }}>
        <InputGroup>
          <InputLeftElement
            pointerEvents="none"
            children={<Icons.Search size={24} color="gray.300" />}
          />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search templates"
            sx={{
              backgroundColor: '#ffffff',
              ':focus': {
                borderColor: 'rgb(255, 111, 15)',
                boxShadow: '0 0 0 1px rgb(255, 111, 15)',
              },
            }}
          />
          <TemplateFilter
            setFilter={setFilter}
            sizeFormats={sizeFormats}
            templates={premadeTemplates}
          />
        </InputGroup>
      </Box>

      <Box
        sx={{
          color: '#ffffff',
          flex: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
          }}
        >
          <CustomScroller>
            <Box sx={{ color: '#ffffff', padding: '0 1.5rem' }}>
              <Text fontWeight="700" fontSize="14px">
                Click to start with one of our ad templates
              </Text>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                padding: '2rem',
              }}
            >
              {templates
                .filter(template => isFiltered(template.name))
                .filter(template => {
                  let filterExist = false;
                  Object.keys(filter).forEach(k => {
                    if (!filterExist) {
                      filterExist = filter[k];
                    }
                  });
                  if (!filterExist) {
                    return true;
                  }
                  const currentLabel = template.size_format.label;
                  if (filter[currentLabel]) {
                    return true;
                  } else {
                    return false;
                  }
                })
                .map(template => {
                  return (
                    <Box
                      key={template.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        const { is_portrait, size_format_id, size_format } =
                          template;

                        const args = {
                          size_format: {
                            ...size_format,
                            isPortrait: is_portrait,
                          },
                          size_format_id,
                          userFontsData,
                          template,
                          local: true,
                        };
                        // updateSizeFormat(args.size_format);
                        loadTemplate(args);
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#3c4349',
                        }}
                      >
                        <Box
                          sx={{
                            backgroundImage: template.previewURL,
                            height: '5rem',
                            width: '9rem',
                            backgroundSize: template.is_portrait
                              ? '100%'
                              : '150%',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: template.is_portrait
                              ? 'top 0 left 50%'
                              : 'top 50% left 50%',
                            transition: 'all 0.2s ease',
                            padding: '4rem',
                            ':hover': {
                              backgroundSize: template.is_portrait
                                ? '67%'
                                : '100%',
                            },
                          }}
                        ></Box>
                      </Box>
                      <Box
                        sx={{
                          marginTop: '0.35rem',
                          fontSize: '0.8rem',
                          color: '#858b99',
                          textAlign: 'center',
                        }}
                      >
                        <Box color="rgba(255,255,255,0.8)">{template.name}</Box>
                        <Box sx={{ fontSize: '0.75rem' }}>
                          {template.size_format.label}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </CustomScroller>
        </Box>
      </Box>
    </Box>
  );
}

export default Templates;
